import { useRef, useState } from "react";
import { useMember } from "../../context/memberContext";
import useListFilters from "../../hooks/useListFilters";
import {
  SP,
  acceptInvite,
  declineInvite,
  getMember,
  useInvites,
} from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { formatLongDateTime, getRemoteDataStatus } from "../../utils";
import SVGCheck from "../../icons/Check.svg?react";
import SVGClose from "../../icons/Close.svg?react";
import Page from "../ui/Page";
import Table from "../ui/Table";
import TableButton, { TableButtonCell } from "../ui/TableButton";

const Invites = () => {
  const { i18n } = useTranslations();
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { data, isValidating, error, mutate } = useInvites(
    filters.state,
    aborter.current.signal,
  );
  const items = data?.items ?? [];
  const hasNextPage = data?.next_page !== undefined;
  const status = getRemoteDataStatus({ isValidating, error });

  const { setMember } = useMember();

  return (
    <Page title={i18n.invites.title}>
      <Table
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.invites.columnHeaders.member}</th>
            <th align="left">{i18n.invites.columnHeaders.companies}</th>
            <th align="left">{i18n.invites.columnHeaders.validUntil}</th>
            <th></th>
            <th></th>
          </tr>
        }
        body={items.map((item, index) => (
          <Row
            key={index}
            invite={item}
            onAccept={(invite) =>
              acceptInvite(invite.id).then(() =>
                getMember(invite.memberId).then((m) => {
                  setMember(m);
                  window.location.href = window.location.origin;
                }),
              )
            }
            onDecline={(invite) =>
              declineInvite(invite.id).then(() =>
                mutate({
                  ...data!,
                  items: [...items.filter((inv) => inv.id !== item.id)],
                }),
              )
            }
          />
        ))}
        hasNextPage={hasNextPage}
        remoteDataStatus={status}
      />
    </Page>
  );
};

interface RowProps {
  invite: SP.Invite;
  onAccept: (invite: SP.Invite) => Promise<any>;
  onDecline: (invite: SP.Invite) => Promise<any>;
}

const Row = ({ invite, onAccept, onDecline }: RowProps) => {
  const { i18n } = useTranslations();
  const [isAccepting, setAccepting] = useState(false);
  const [isDeclining, setDeclining] = useState(false);
  return (
    <tr>
      <td>{invite.memberName}</td>
      <td>{invite.companyId ? invite.companyName : i18n.allCompanies}</td>
      <td>{formatLongDateTime(invite.expiresAt)}</td>
      <TableButtonCell>
        <TableButton
          glyph={SVGCheck}
          title={i18n.invites.accept}
          loading={isAccepting}
          onClick={() => {
            setAccepting(true);
            onAccept(invite).finally(() => setAccepting(false));
          }}
        />
      </TableButtonCell>
      <TableButtonCell>
        <TableButton
          glyph={SVGClose}
          title={i18n.invites.decline}
          loading={isDeclining}
          onClick={() => {
            setDeclining(true);
            onDecline(invite).finally(() => setDeclining(false));
          }}
        />
      </TableButtonCell>
    </tr>
  );
};

export default Invites;
